import { useQuery } from '@tanstack/react-query';

import { useOrganization } from '@/contexts/OrganizationContext';
import type { RuntimeAdvisoriesFlagStatus } from '@/hooks/useRuntimeAdvisoriesFlag';
import { ADVISORIES_SHELL_QUERY_KEY } from '@/lib/advisoryQueries';
import { getAdvisoriesShell, getMspAdvisoriesShell } from '@/lib/runtimeAdvisories';
import type { FeatureReleaseStage } from '@/components/ui/feature-stage-badge';

export interface AdvisoriesShellState {
  /** True only once the server resolved the customer/MSP-shell entry ON for this user. */
  enabled: boolean;
  /** True while the user or the server answer is still loading: render nothing, never redirect. */
  loading: boolean;
  unread: number;
  releaseStage: FeatureReleaseStage;
  status: RuntimeAdvisoriesFlagStatus;
  retry: () => void;
}

/** ENT-6091: exposure and unread state belong to the active organisation or MSP context.
 * Keep pending, unavailable and disabled answers distinct; context changes have separate caches.
 */
export function useAdvisoriesShell(mspView = false, readEnabled = true): AdvisoriesShellState {
  const { user, organization, isMspContext, mspHomeOrgId, isLoading: organizationLoading } = useOrganization();
  const userId = user?.id ?? null;
  const organizationId = mspView && isMspContext ? mspHomeOrgId : organization?.id ?? null;
  const query = useQuery({
    queryKey: [ADVISORIES_SHELL_QUERY_KEY, mspView ? 'msp' : 'org', userId, organizationId],
    queryFn: () => mspView ? getMspAdvisoriesShell(organizationId ?? '') : getAdvisoriesShell(organizationId ?? ''),
    enabled: Boolean(userId && organizationId) && !organizationLoading && readEnabled,
    staleTime: 60_000,
    retry: false,
  });

  const retry = () => { void query.refetch(); };
  if (organizationLoading || !readEnabled) return { enabled: false, loading: true, unread: 0, releaseStage: null, status: 'loading', retry };
  if (!userId || !organizationId) return { enabled: false, loading: organizationLoading, unread: 0, releaseStage: null, status: organizationLoading ? 'loading' : 'resolved', retry };
  if (query.isError) return { enabled: false, loading: false, unread: 0, releaseStage: null, status: 'error', retry };
  if (!query.isSuccess) return { enabled: false, loading: true, unread: 0, releaseStage: null, status: 'loading', retry };
  const data = query.data;
  // A transport success is not a resolved flag: both RPCs return a boolean
  // exposure and a nonnegative count(*). Unknown data must preserve the CTA for Retry.
  if (!data || typeof data !== 'object' || Array.isArray(data)
    || typeof data.enabled !== 'boolean' || typeof data.unread !== 'number'
    || !Number.isInteger(data.unread) || data.unread < 0) {
    return { enabled: false, loading: false, unread: 0, releaseStage: null, status: 'error', retry };
  }
  const releaseStage: FeatureReleaseStage = data.release_stage === 'new' || data.release_stage === 'alpha' || data.release_stage === 'beta' ? data.release_stage : null;
  return { enabled: data.enabled, loading: false, status: 'resolved', retry, unread: data.enabled ? data.unread : 0, releaseStage: data.enabled ? releaseStage : null };
}

/** ENT-6091: MSP menu exposure follows managed customers, independent of the MSP own-org flag. */
export function useMspAdvisoriesShell() { return useAdvisoriesShell(true); }
